import {
  BulkOrderSuppliersInputModel,
  OrderSuppliersViewModel,
  SupplierUploadInputModel,
  SupplierUploadLogInputModel,
  SupplierUploadViewModel,
  WorkflowOrderKind,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { read, utils } from 'xlsx'
import { useAuth } from '../../auth/UseAuth'
import { FileHelper } from '../../helpers/FileHelper'
import { ObjectHelper } from '../../helpers/ObjectHelper'
import { printDate } from '../../helpers/Utils'
import { useGetTenantsQuery } from '../../redux/api/customersApi'
import {
  useGetSupplierUploadsQuery,
  useLazyGetOrdersSuppliersQuery,
  useLazyGetSupplierUploadQuery,
  usePostSupplierUploadMutation,
} from '../../redux/api/ordersApi'
import Alert from '../common/Alert/Alert'
import { IconCell } from '../common/Grid/Cells/IconCell/IconCell'
import { Grid, GridColumn } from '../common/Grid/Grid'
import { LogsModal } from './LogsModal/LogsModal'
import { Arr } from '../../helpers/Arr'
import { DocumentsDropzone } from './DocumentsDropzone/DocumentsDropzone'
import { Loader } from '../common/Loader/Loader'

const COLUMNS = [
  'Kundenbestellnummer',
  'Externe Fahrzeugnummer',
  'Bestell-Nr.',
  'Modellcode',
  'Modellbezeichnung',
  'Händler',
  'H_Ort_Name',
  'Modelljahr',
  'Farbcodes',
  'Primärstatus',
  'Aktuelle SG-Woche ab',
  'Standort',
  'Chassis-Nr',
  'Stammnummer',
  'S060-Datum',
  'Mofis-Datum (Immatdatum)',
  'Fakturadatum',
  'Typenschein-Nr',
  'Kohlendioxid in g/km',
  'Anlegedatum',
  'Kaufvertragsdatum',
  'Warenempfänger',
  'Marke',
  'Verzollungsdatum',
  'Datum Fahrzeugeingang',
  'Datum Fahrzeugabruf',
  'Übergabedatum an Cotra',
  'Lieferdatum an Händler',
  'Ausstattung',
  'Ausstattung Teil 2',
  'Liefercode',
  'CRM Flottenprojekt Nr.',
  'Angelegt von',
  'Endkundenstatus',
  'Flottenkunde-Name',
  'Flottenkundennummer',
  'Kennzeichen "Accountplan-Fahrzeug"',
  'Kennzeichen "Umbauten"',
  'Kennzeichen "werksgestütztes Fahrzeug"',
  'Typenscheinnr. von ASTRA/MOFIS',
  'Fakturastatus',
  'Datum des Primärstatus',
  'Bonusbasis',
  'Warenempfänger Name',
] as const

const TENANTS = {
  Amag: {
    id: '66fb19d5-7dcf-493a-a9e8-08dbad08d597',
    buildFile: false,
  },
  Peugeot: {
    id: '9df00603-fd32-4c8b-983c-3176b0cb4f5c',
    buildFile: true,
  },
}

const CSV_MAPPINGS: Record<keyof typeof TENANTS, Partial<Record<ColumnName, string | number>>> = {
  Amag: {
    Kundenbestellnummer: 0,
    'Externe Fahrzeugnummer': 1,
  },
  Peugeot: {
    Kundenbestellnummer: 'Händler-Referenz',
    'Externe Fahrzeugnummer': 'Auftrag Nr.',
    Modellcode: 'LCDV',
    'Chassis-Nr': 'Fahrgestell-Nr.',
    Fakturadatum: 'Datum',
  },
}

const ORDER_MAPPINGS: Record<keyof typeof TENANTS, Partial<Record<ColumnName, string>>> = {
  Amag: {
    'Externe Fahrzeugnummer': 'AMAG-Nr.',
    Modellcode: 'model_code',
    'Chassis-Nr': 'Chassis_NR',
    H_Ort_Name: 'Haendlername',
    Fakturadatum: 'Lieferung_Galliker',
    'Warenempfänger Name': 'amag_subsidiary',
  },
  Peugeot: {
    'Externe Fahrzeugnummer': 'Supplier_NR',
    Modellcode: 'model_code',
    'Chassis-Nr': 'Chassis_NR',
    Fakturadatum: 'Lieferung_Galliker',
  },
}

const CONSTRAINTS: Record<keyof typeof TENANTS, Partial<Record<ColumnName, { mandatory?: boolean; type?: 'date' }>>> = {
  Amag: {
    Kundenbestellnummer: { mandatory: true },
    'Externe Fahrzeugnummer': { mandatory: true },
    'Bestell-Nr.': { mandatory: true },
    'S060-Datum': { type: 'date' },
    'Mofis-Datum (Immatdatum)': { type: 'date' },
    Fakturadatum: { type: 'date' },
    Anlegedatum: { type: 'date' },
    Kaufvertragsdatum: { type: 'date' },
    Verzollungsdatum: { type: 'date' },
    'Datum Fahrzeugeingang': { type: 'date' },
    'Datum Fahrzeugabruf': { type: 'date' },
    'Übergabedatum an Cotra': { type: 'date' },
    'Lieferdatum an Händler': { type: 'date' },
    'Datum des Primärstatus': { type: 'date' },
  },
  Peugeot: {
    Kundenbestellnummer: { mandatory: true },
    'Externe Fahrzeugnummer': { mandatory: true },
    Fakturadatum: { type: 'date' },
  },
}

type ColumnName = (typeof COLUMNS)[number]

type CsvRow = {
  [key in ColumnName]?: string
}

export function OrderUpload() {
  const { user } = useAuth()
  const [alertMessage, setAlertMessage] = useState<string>()
  const [isParsing, setIsParsing] = useState<boolean>(false)
  const [selectedTenant, setSelectedTenant] = useState<string | undefined>()
  const [logsModalData, setLogsModalData] = useState<SupplierUploadViewModel>()
  const [loadOrdersSuppliers] = useLazyGetOrdersSuppliersQuery()
  const tenants = useGetTenantsQuery(undefined, { skip: !user?.isInternalUser })
  const [postSupplierUpload, postSupplierUploadResult] = usePostSupplierUploadMutation()
  const supplierUploads = useGetSupplierUploadsQuery()
  const [getSupplierUpload, getSupplierUploadResult] = useLazyGetSupplierUploadQuery()
  const validTenants = tenants.data?.filter((t) => t.tags.includes('VehicleProductionUpload')) ?? []
  const { t } = useTranslation()

  const loading =
    postSupplierUploadResult.isLoading ||
    getSupplierUploadResult.isFetching ||
    supplierUploads.isFetching ||
    tenants.isFetching ||
    isParsing

  useEffect(() => {
    if (user?.isInternalUser && tenants.data) {
      const validTenant = tenants.data.find((t) => t.tags.includes('VehicleProductionUpload'))
      setSelectedTenant(validTenant?.id)
    } else {
      setSelectedTenant(user?.tenant)
    }
  }, [user, tenants.data])

  const compare = (first: string, second: string) => {
    return first.toLowerCase().replace(/[\W_]/g, '') === second.toLowerCase().replace(/[\W_]/g, '')
  }

  const validateValue = (row: CsvRow, column: ColumnName, tenantKey: keyof typeof TENANTS) => {
    const value = row[column]
    const constraints = CONSTRAINTS[tenantKey][column]
    const columnKey = CSV_MAPPINGS[tenantKey][column] ?? column
    const columnName = typeof columnKey === 'string' ? columnKey : column
    if (constraints?.type === 'date' && value) {
      const pattern = /(\d{2})\.(\d{2})\.(\d{4}).*$/
      if (pattern.test(value)) {
        const date = new Date(value.replace(pattern, '$3-$2-$1'))
        if (!isNaN(date.getTime())) {
          row[column] = value.replace(pattern, '$1.$2.$3')
          return 'valid'
        }
      }
      return `Invalid date - ${columnName}`
    }
    return 'valid'
  }

  const matchField = (csvColumn: ColumnName, order: OrderSuppliersViewModel, tenantKey: keyof typeof TENANTS) => {
    const orderMapping = ORDER_MAPPINGS[tenantKey][csvColumn]
    if (!orderMapping) {
      return undefined
    }
    return order.form.fields.find((f) => compare(f.name, orderMapping))
  }

  const getFinalCsvFile = async (file: File, tenantKey: keyof typeof TENANTS) => {
    const tenant = TENANTS[tenantKey]
    if (!tenant.buildFile) {
      return file
    }
    const mapping = CSV_MAPPINGS[tenantKey]
    const buffer = await file.arrayBuffer()
    const workbook = read(buffer, { type: 'buffer', raw: true })
    const sheetName = workbook.SheetNames[0]
    const sheet = workbook.Sheets[sheetName]
    const sheetRows = utils.sheet_to_json<string[]>(sheet, { header: 1, dateNF: 14 })
    const sheetColumns = sheetRows[0]
    const result: string[][] = [[...COLUMNS]]
    const columnIndexes = COLUMNS.map((c) => {
      const key = mapping[c]
      return sheetColumns.findIndex(
        (name, index) =>
          (typeof key === 'number' && key === index) ||
          (typeof key === 'string' && compare(key, name)) ||
          compare(c, name)
      )
    })
    for (let rowIdx = 1; rowIdx < sheetRows.length; rowIdx++) {
      const row: string[] = []
      result.push(row)
      for (let i = 0; i < columnIndexes.length; i++) {
        const colIdx = columnIndexes[i]
        if (colIdx > -1) {
          row.push(sheetRows[rowIdx][colIdx])
        } else {
          row.push('')
        }
      }
    }

    const finalSheet = utils.aoa_to_sheet(result)
    const csv = utils.sheet_to_csv(finalSheet, { FS: ';' })
    const blob = new Blob([csv], { type: 'text/csv' })
    const timestamp = new Date().toISOString().slice(0, 10).replaceAll('-', '')
    const fileName = `${timestamp}_${tenantKey.toUpperCase()}_INTERFACE.csv`
    return new File([blob], fileName, { type: 'text/csv' })
  }

  const handleFileDropped = async (selectedFiles: FileList) => {
    const selectedFile = selectedFiles.item(0)
    if (!selectedFile) {
      return
    }

    setIsParsing(true)
    try {
      const tenantKeys = Object.keys(TENANTS) as (keyof typeof TENANTS)[]
      const tenantKey = tenantKeys.find((key) => selectedTenant && selectedTenant === TENANTS[key].id)
      if (!tenantKey) {
        setAlertMessage(`Invalid tenant`)
        return
      }

      const tenant = TENANTS[tenantKey]
      const mapping = CSV_MAPPINGS[tenantKey]

      const file = await getFinalCsvFile(selectedFile, tenantKey)
      const buffer = await file.arrayBuffer()
      const workbook = read(buffer, { type: 'buffer', raw: true })
      const sheetName = workbook.SheetNames[0]
      if (!sheetName) {
        setAlertMessage('Invalid CSV/XLSX file selected!')
        return
      }
      const sheet = workbook.Sheets[sheetName]
      const sheetRows = utils.sheet_to_json<string[]>(sheet, { header: 1, dateNF: 14 })
      const sheetColumns = sheetRows[0]
      const expectedColumns = [...COLUMNS]

      const duplicateColumns = sheetColumns.filter((name, index) => {
        return sheetColumns.some((dupName, dupIndex) => index !== dupIndex && compare(name, dupName))
      })

      if (duplicateColumns.length) {
        setAlertMessage(`Duplicate columns: ${duplicateColumns.join(', ')}`)
        return
      }

      const columnIndexes = expectedColumns.map((c) => {
        const key = mapping[c]
        return sheetColumns.findIndex(
          (name, index) =>
            (typeof key === 'number' && key === index) ||
            (typeof key === 'string' && compare(key, name)) ||
            compare(c, name)
        )
      })

      const missingColumns = expectedColumns.filter((_c, i) => columnIndexes[i] < 0).map((c) => mapping[c] ?? c)

      if (missingColumns.length) {
        setAlertMessage(`Missing columns: ${missingColumns.join(', ')}`)
        return
      }

      const rows = sheetRows.slice(1).map((row) =>
        columnIndexes.reduce((obj, index, columnIdx) => {
          obj[expectedColumns[columnIdx]] = row[index]
          return obj
        }, {} as CsvRow)
      )

      const validatedRows: { valid: CsvRow[]; [key: string]: CsvRow[] } = {
        valid: [],
      }

      for (const row of rows) {
        let validationResult = 'valid'
        for (const column of expectedColumns) {
          validationResult = validateValue(row, column, tenantKey)
          if (validationResult !== 'valid') {
            break
          }
        }
        if (!validatedRows[validationResult]) {
          validatedRows[validationResult] = []
        }
        validatedRows[validationResult].push(row)
      }

      console.debug({ validatedRows })

      const orders = await loadOrdersSuppliers({ tenantId: tenant.id }).unwrap()

      const validOrders = orders
        .filter((o) => o.kind === WorkflowOrderKind.Outgoing && o.tenantId === tenant.id && o.logisticId)
        .map(ObjectHelper.clone)

      console.debug({ validOrders })

      const attachment = await FileHelper.fileToDataURL(file)
      let sftpAttachment: string | null = null

      if (tenant.buildFile) {
        sftpAttachment = await FileHelper.fileToDataURL(selectedFile)
      }

      const supplierUpload: SupplierUploadInputModel = {
        tenantId: tenant.id,
        orders: [],
        attachmentName: file.name,
        attachmentContent: `${file.name}|${attachment!}`,
        sftpAttachmentContent: sftpAttachment ?? undefined,
        logs: [],
      }

      const statusChanges = Arr.toObject(
        Object.values(WorkflowOrderStepStatus),
        (s) => s,
        () => 0
      )

      const mandatoryColumns = expectedColumns.filter((c) => CONSTRAINTS[tenantKey][c]?.mandatory)

      const matches: Record<number, WorkflowOrderStepStatus | boolean | 'empty' | undefined> = {}

      const validRows = validatedRows.valid

      for (const order of validOrders) {
        const updatedOrder: BulkOrderSuppliersInputModel = {
          id: order.id,
          form: { fields: [], status: order.form.status },
        }
        const csvRowIndex = validRows.findIndex(
          (r) => r.Kundenbestellnummer && r.Kundenbestellnummer === order.logisticId
        )
        if (csvRowIndex < 0) {
          continue
        }

        const csvRow = validRows[csvRowIndex]

        if (mandatoryColumns.some((c) => !csvRow[c])) {
          matches[csvRowIndex] = 'empty'
          continue
        }

        if (matches[csvRowIndex] === undefined) {
          matches[csvRowIndex] = false
        }

        if (order.form.status === 'Done' || order.form.status === 'Cancelled') {
          if (matches[csvRowIndex] !== true) {
            matches[csvRowIndex] = order.form.status
          }
          continue
        }
        for (const entry of Object.entries(csvRow)) {
          const csvColName = entry[0] as ColumnName
          const csvColValue = entry[1] ?? ''
          const orderMapping = ORDER_MAPPINGS[tenantKey][csvColName]
          if (!orderMapping) {
            continue
          }
          const field = order.form.fields.find(
            (f) => !f.readonly && f.type !== 'upload' && compare(f.name, orderMapping)
          )
          if (!field) {
            continue
          }
          const fieldValue = field.value ?? ''
          if (fieldValue !== csvColValue) {
            field.value = csvColValue
            updatedOrder.form.fields.push({
              name: field.name,
              value: field.value,
              documents: [],
            })
          }
        }
        const initialStatus = order.form.status
        if (updatedOrder.form.fields.length) {
          matches[csvRowIndex] = true
          const amagNr = matchField('Externe Fahrzeugnummer', order, tenantKey)
          if (order.form.status === WorkflowOrderStepStatus.New && amagNr?.value) {
            order.form.status = WorkflowOrderStepStatus.InProgress
          }
          if (order.form.status === WorkflowOrderStepStatus.InProgress) {
            const chassisNr = matchField('Chassis-Nr', order, tenantKey)
            const fakturaDatum = matchField('Fakturadatum', order, tenantKey)
            const stammnummer = matchField('Stammnummer', order, tenantKey)
            if (chassisNr?.value && fakturaDatum?.value && stammnummer?.value) {
              order.form.status = WorkflowOrderStepStatus.Done
            }
          }
          if (order.form.status !== initialStatus) {
            statusChanges[order.form.status]!++
          }
          updatedOrder.form.status = order.form.status
          supplierUpload.orders.push(updatedOrder)
        }
      }

      const statusChangeLogs = Object.keys(statusChanges)
        .filter((k) => statusChanges[k])
        .map((k) => {
          return {
            type: 'info',
            message: `${statusChanges[k]} orders set to "${k}"`,
          } as SupplierUploadLogInputModel
        })

      const invalidRowLogs = Object.keys(validatedRows)
        .filter((k) => k !== 'valid')
        .map((k) => {
          return {
            message: `${validatedRows[k].length} skipped rows (${k})`,
            type: 'error',
          } as SupplierUploadLogInputModel
        })

      const matchValues = Object.values(matches)
      const noMatchCount = validRows.length - matchValues.length
      const noChangeCount = matchValues.filter((m) => m === false).length
      const doneCount = matchValues.filter((m) => m === WorkflowOrderStepStatus.Done).length
      const cancelledCount = matchValues.filter((m) => m === WorkflowOrderStepStatus.Cancelled).length
      const emptyCount = matchValues.filter((m) => m === 'empty').length

      supplierUpload.logs.push({ message: `${rows.length} total rows`, type: 'info' })
      if (noMatchCount > 0) {
        supplierUpload.logs.push({ message: `${noMatchCount} skipped rows (no match)`, type: 'info' })
      }
      if (emptyCount > 0) {
        supplierUpload.logs.push({ message: `${emptyCount} skipped rows (new order)`, type: 'info' })
      }
      if (noChangeCount > 0) {
        supplierUpload.logs.push({ message: `${noChangeCount} skipped rows (no changes)`, type: 'info' })
      }
      if (doneCount > 0) {
        supplierUpload.logs.push({ message: `${doneCount} skipped rows (order done)`, type: 'info' })
      }
      if (cancelledCount > 0) {
        supplierUpload.logs.push({ message: `${cancelledCount} skipped rows (order cancelled)`, type: 'info' })
      }
      supplierUpload.logs.push(...invalidRowLogs)
      supplierUpload.logs.push({
        message: `${supplierUpload.orders.length} updated orders`,
        type: supplierUpload.orders.length ? 'success' : 'info',
      })
      supplierUpload.logs.push(...statusChangeLogs)
      postSupplierUpload(supplierUpload)
    } catch {
      setAlertMessage('Invalid CSV/XLSX file selected!')
    } finally {
      setIsParsing(false)
    }
  }

  const downloadAttachment = useCallback(
    async (supplierUploadId: string) => {
      try {
        const supplierUpload = await getSupplierUpload(supplierUploadId).unwrap()
        if (supplierUpload.attachmentContent && supplierUpload.attachmentContentType && supplierUpload.attachmentName) {
          FileHelper.downloaBase64File(
            supplierUpload.attachmentContent,
            supplierUpload.attachmentName,
            supplierUpload.attachmentContentType
          )
        }
      } catch (e) {
        console.error(e)
      }
    },
    [getSupplierUpload]
  )

  const supplierUploadsColumns = useMemo(() => {
    const columns: GridColumn<SupplierUploadViewModel>[] = [
      {
        key: 'attachmentId',
        name: '',
        frozen: true,
        minWidth: 50,
        maxWidth: 50,
        renderCell: (data) => <IconCell icon="icon-download" onClick={() => downloadAttachment(data.row.id)} />,
      },
      {
        key: 'id',
        name: '',
        minWidth: 50,
        maxWidth: 50,
        renderCell: (data) => {
          const hasSuccess = data.row.logs.some((l) => l.type === 'success')
          const hasError = data.row.logs.some((l) => l.type === 'error')
          return (
            <IconCell
              onClick={() => setLogsModalData(data.row)}
              size={20}
              color={hasError ? 'red' : hasSuccess ? '#39b54a' : 'grey'}
              icon={hasError ? 'icon-warning' : 'icon-info'}
            />
          )
        },
      },
      {
        key: 'attachmentName',
        name: t('NAME')!,
        minWidth: 350,
        maxWidth: 700,
        sortBy: (item) => item.attachmentName,
        filterBy: (item) => item.attachmentName,
        exportFormat: (item) => item.attachmentName,
      },
      {
        key: 'createdAt',
        name: t('CREATED')!,
        minWidth: 150,
        renderCell: (data) => printDate(data.row.createdAt, true),
        sortBy: (item) => new Date(item.createdAt),
        filterBy: (item) => printDate(item.createdAt, true),
        exportFormat: (item) => printDate(item.createdAt, true),
      },
      {
        key: 'createdBy',
        name: t('USER')!,
        minWidth: 229,
        maxWidth: 229,
        sortBy: (item) => item.createdBy,
        filterBy: (item) => item.createdBy,
        exportFormat: (item) => item.createdBy,
      },
    ]
    return columns
  }, [downloadAttachment, t])

  const supplierUploadRows = useMemo(() => {
    if (!selectedTenant || !supplierUploads.data) {
      return []
    }
    return supplierUploads.data.filter((u) => u.tenantId === selectedTenant)
  }, [selectedTenant, supplierUploads.data])

  return (
    <Loader blocking={loading}>
      <div className="container">
        <div className="row">
          <div className="col-12 col-md-9 uploadArea">
            <Grid
              id="SupplierUploads"
              title="Uploads"
              icon="icon-excel-import"
              columns={supplierUploadsColumns}
              rows={supplierUploadRows}
              rowKeyGetter={(item) => item.id}
            />
          </div>
          <div className="col-12 col-md-3">
            {validTenants.length > 1 && (
              <div className="selectViewArea">
                <div className="selectArea">
                  <select
                    defaultValue={selectedTenant}
                    value={selectedTenant}
                    onChange={(e) => setSelectedTenant(e.target.value)}
                  >
                    {validTenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <i className="icon-bottom" />
                </div>
              </div>
            )}
            <DocumentsDropzone fileDropped={handleFileDropped} />
          </div>
        </div>
      </div>
      <LogsModal supplierUpload={logsModalData} onClose={() => setLogsModalData(undefined)} />
      <Alert
        visible={!!alertMessage}
        onClose={() => setAlertMessage(undefined)}
        title={''}
        message={alertMessage ?? ''}
      />
    </Loader>
  )
}
